// src/components/trades/analysis/SalaryCapSection.js
import React from 'react';
import { dynastyTheme } from '../../../services/colorService';
import { DollarSign, TrendingUp, TrendingDown, Calendar } from 'lucide-react';

export const SalaryCapSection = ({ data }) => {
  if (!data) return null;

  const { proposing_team, receiving_team } = data;

  const renderTeamCap = (teamData, teamName) => {
    const currentChange = teamData.change || 0;
    const nextYearChange = teamData.next_year_change || 0;
    const currentIsPositive = currentChange > 0;
    const nextYearIsPositive = nextYearChange > 0;

    return (
      <div className="flex-1">
        <h4 className="text-lg font-semibold text-white mb-3">{teamName}</h4>
        
        {/* Current Year Section */}
        <div className="mb-4 pb-4 border-b border-gray-700">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-sm font-semibold text-gray-300">Current Year (Already Paid)</span>
          </div>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between items-center">
              <span className="text-gray-400">Total Salary:</span>
              <span className="text-white font-mono">${teamData.current_cap?.toFixed(2) || '0.00'}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-400">Salary Swap:</span>
              <span className={`font-mono flex items-center gap-1 ${currentIsPositive ? 'text-emerald-400' : 'text-red-400'}`}>
                {currentIsPositive ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                {currentIsPositive ? '+' : ''}${currentChange.toFixed(2)}
              </span>
            </div>
            <div className="text-xs text-gray-500 space-y-1 pl-2">
              <div>• Gives: ${teamData.gives_cost?.toFixed(2) || '0.00'}</div>
              <div>• Gets: ${teamData.receives_cost?.toFixed(2) || '0.00'}</div>
            </div>
          </div>
        </div>

        {/* Next Year Section - The Important One */}
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Calendar className="w-4 h-4 text-blue-400" />
            <span className="text-sm font-semibold text-blue-400">Next Year Cap Impact</span>
          </div>
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-gray-400">Current Projection:</span>
              <span className="text-white font-mono">${teamData.next_year_cap?.toFixed(2) || '0.00'}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-400">Post-Trade Cap:</span>
              <span className="text-white font-mono">${teamData.next_year_post_trade?.toFixed(2) || '0.00'}</span>
            </div>
            <div className="flex justify-between items-center pt-2 border-t border-gray-700">
              <span className="text-gray-400 font-semibold">Cap Change:</span>
              <span className={`font-mono font-bold flex items-center gap-1 ${nextYearIsPositive ? 'text-emerald-400' : 'text-red-400'}`}>
                {nextYearIsPositive ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
                {nextYearIsPositive ? '+' : ''}${nextYearChange.toFixed(2)}
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className={`p-4 ${dynastyTheme.classes.bg.darkCard} rounded-lg border ${dynastyTheme.classes.border.neutral}`}>
      <h3 className={`text-lg font-bold ${dynastyTheme.classes.text.primary} mb-4 flex items-center gap-2`}>
        <DollarSign className="w-5 h-5" />
        Salary Cap Impact
      </h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {renderTeamCap(proposing_team, "Your Team")}
        {renderTeamCap(receiving_team, "Their Team")}
      </div>
    </div>
  );
};

export default SalaryCapSection;
